import GtfsRealtimeBindings from "gtfs-realtime-bindings";
import Utilities from "./lib/utilities";

const GtfsRealtime = (() => {
	'use strict';

	let map,
		realtimeMarkers = [],
		visibleTrips,
		visibleStop,
		intervalId,
		parser = new DOMParser(),
		$dropdownDestination = document.getElementById( 'tm__destination' ),
		$nextArrival = document.getElementById( 'tm__stop' ).querySelector( '.tm__next-arrival' ),
		$nextArrivalMinutes = $nextArrival.querySelector( "strong" ),
		tripUpdates = {};
	const $sidebar = document.getElementById( 'tm__sidebar' );

	const constructor = () => {
		map = TransitMap.getProperty( 'map' );

		$sidebar.addEventListener( 'tm__change-panel', ( e ) => {
			let pane = e.detail.pane.split( '|' ),
				paneId = parseInt( pane[1], 10 );
			switchToPanel( e.detail.panel, paneId );
		} );

		// immediately update/hide markers when changing tabs (instead of waiting up to 10 seconds for the automatic update)
		$sidebar.addEventListener( 'tm__change-tab', () => {
			continualGtfsUpdate();
		} );

		$dropdownDestination.addEventListener( "change", () => {
			$nextArrival.hidden = true;
			fetchTripUpdates();
		} );

		// need to init the realtime data specially if we're loading the map page directly into a route or stop
		maybeInitRealtimeOnLoad();

		renderServiceAlerts();
	}

	const getPinSvg = ( bearing = 0 ) => {
		// generate the custom real-time bus pin
		// the entire pin is rotated to match the bearing of the vehicle and the bus icon inside the pin is rotated exactly opposite so it stays upright
		const rotation = parseInt( bearing, 10 ) + 180, // bearing is measured from north, but our svg starts pointing south
			svg = '<svg style="transform-origin: bottom center; transform: rotate(' + rotation + 'deg)" xmlns="http://www.w3.org/2000/svg" width="45" height="60">' +
				'<path fill="#212121" d="M 0,22.5 C 0,10.0746 10.0746,0 22.5,0 34.9254,0 45,10.0746 45,22.5 c 0,9.0672 -3.1623,11.6138 -20.1772,36.2966 -1.1194,1.6232 -3.4982,1.6232 -4.6176,0 C 3.16231,34.1138 0,31.5672 0,22.5 Z"/>' +
				'<path style="transform-origin: 50% 37.9%; transform: rotate(-' + rotation + 'deg)" fill="#fff" d="m 22.5,7.9198 c 7.7519,0 12.9851,2.0989 12.9851,4.6455 v 2.7705 c 0.9795,0 1.847,0.8676 1.847,1.8471 v 3.722 c 0,1.0354 -0.8675,1.847 -1.847,1.847 v 9.263 c 0,1.0355 -0.8676,1.8471 -1.847,1.8471 v 1.847 c 0,1.0354 -0.8676,1.847 -1.8471,1.847 h -1.847 c -1.0354,0 -1.847,-0.8116 -1.847,-1.847 V 33.862 H 16.959 v 1.847 c 0,1.0354 -0.8676,1.847 -1.8471,1.847 h -1.847 c -1.0354,0 -1.847,-0.8116 -1.847,-1.847 v -1.847 c -1.0354,0 -1.847,-0.8116 -1.847,-1.8471 v -9.263 c -1.03545,0 -1.84702,-0.8116 -1.84702,-1.847 v -3.722 c 0,-0.9795 0.81157,-1.8471 1.84702,-1.8471 v -2.7705 c 0,-2.5466 5.1492,-4.6455 12.9851,-4.6455 z m -9.2631,14.8321 c 0,1.0354 0.8116,1.847 1.8471,1.847 h 6.4925 V 15.3358 H 15.084 c -1.0355,0 -1.8471,0.8676 -1.8471,1.8471 z m 10.1866,1.847 h 6.4925 c 0.9795,0 1.8471,-0.8116 1.8471,-1.847 v -5.569 c 0,-0.9795 -0.8676,-1.8471 -1.8471,-1.8471 h -6.4925 z m -9.2631,6.4925 c 0.9795,0 1.8471,-0.8115 1.8471,-1.847 0,-0.9795 -0.8676,-1.847 -1.8471,-1.847 -1.0354,0 -1.847,0.8675 -1.847,1.847 0,1.0355 0.8116,1.847 1.847,1.847 z m 16.6792,0 c 0.9794,0 1.847,-0.8115 1.847,-1.847 0,-0.9795 -0.8676,-1.847 -1.847,-1.847 -1.0355,0 -1.8471,0.8675 -1.8471,1.847 0,1.0355 0.8116,1.847 1.8471,1.847 z M 27.1175,11.6138 h -9.263 c -0.5317,0 -0.9235,0.4758 -0.9235,0.9235 0,0.5318 0.3918,0.9235 0.9235,0.9235 h 9.263 c 0.4758,0 0.9235,-0.3917 0.9235,-0.9235 0,-0.4757 -0.4757,-0.9235 -0.9235,-0.9235 z"/>' +
				'</svg>';

		return parser.parseFromString( svg, 'image/svg+xml' ).documentElement;
	};

	const renderServiceAlerts = () => {
		// get list of alerts urls to fetch, filtering out undefined and duplicate values
		const alertsUrls = [...new Set( Object.values( gtfsRt ).map( agency => agency.alerts ).filter( url => url !== undefined ) )],
			overviewAlertsPane = document.getElementById( 'tab-alerts' ).querySelector( '[data-pane="overview_alerts"]' ),
			detailAlertsPane = document.getElementById( 'tab-alerts' ).querySelector( '[data-panel="alert_detail"] .tm__panel-inner' );
		if ( !alertsUrls.length || !overviewAlertsPane || !detailAlertsPane ) {
			overviewAlertsPane.classList.remove( 'is-loading' );
			return;
		}

		let alerts = [];
		const fetchPromises = alertsUrls.map( url =>
			fetch( url )
				.then( response => response.arrayBuffer() )
				.then( buffer => {
					const feedMessage = GtfsRealtimeBindings.transit_realtime.FeedMessage.decode(
						new Uint8Array( buffer )
					);
					alerts = getAlerts( feedMessage, alerts );
				} )
				.catch( err => {
					console.warn( 'Error:', err );
				} )
		);

		Promise.all( fetchPromises ).then( () => {
			const [alertOverview, alertDetail, alertCount] = renderAlerts( alerts );

			overviewAlertsPane.classList.remove( 'is-loading' );
			overviewAlertsPane.innerHTML = '<ul class="tm__expandable-list">' + alertOverview + '</ul>';
			detailAlertsPane.insertAdjacentHTML( 'beforeend', alertDetail );
			if ( alertCount ) $sidebar.querySelector( 'a[href="?tab=alerts"]' ).insertAdjacentHTML( 'beforeend', `<sup>${alertCount}</sup>` );

			// on pageload, show detail pane if it's already supposed to be open
			const params = new URLSearchParams( location.search );
			if ( params.get( "tab" ) === 'alerts' ) {
				const agencyId = params.get( "agency" ) ? parseInt( params.get( "agency" ), 10 ) : null;
				if ( agencyId ) {
					const $pane = document.getElementById( 'tab-alerts' ).querySelector( '[data-pane^="agency-alerts|' + agencyId + '"]' );
					if ( $pane ) {
						// this agency has alerts that were just rendered; show them manually
						$pane.setAttribute( 'aria-hidden', 'false' );
						$pane.removeAttribute( 'inert' );
					} else {
						// no alerts to show for this agency (or this agency doesn't exist); show a message instead of nothing
						const noAlerts = `<div data-pane="agency-alerts|${agencyId}" aria-hidden="false"><p>No active alerts for this agency.</p></div>`;
						document.getElementById( 'tab-alerts' ).querySelector( '.tm__panel--alert_detail .tm__panel-inner' ).insertAdjacentHTML( 'beforeend', noAlerts );
					}
				}
			}
		} );
	}

	const getAlerts = ( feedMessage, alerts ) => {
		const now = Math.floor( Date.now() / 1000 ),
			isTimeWithinRange = ( now, ranges ) => {
				return ranges.some( ( {start, end} ) => {
					return now >= (start || 0) && now <= (end || now);
				} )
			};

		feedMessage.entity.forEach( entity => {
			const isWithinRange = isTimeWithinRange( now, entity.alert.activePeriod );
			if ( !isWithinRange ) return;

			// get list of agencyIds this alert applies to, filtering out undefined and duplicate values
			const agencyIds = [...new Set( entity.alert.informedEntity.map( entity => parseInt( entity.agencyId, 10 ) ).filter( id => !!id ) )];

			agencyIds.forEach( agencyId => {
				const routeIds = [...new Set( entity.alert.informedEntity.filter( entity => entity.agencyId === agencyId.toString() ).map( entity => parseInt( entity.routeId, 10 ) ).filter( id => !!id ) )];
				const stopIds = [...new Set( entity.alert.informedEntity.filter( entity => entity.agencyId === agencyId.toString() ).map( entity => parseInt( entity.stopId, 10 ) ).filter( id => !!id ) )];
				alerts[agencyId] = alerts[agencyId] || [];
				alerts[agencyId].push( {
					routeIds: routeIds,
					stopIds: stopIds,
					header: entity.alert?.headerText?.translation[0]?.text,
					description: entity.alert?.descriptionText?.translation[0]?.text,
					url: entity.alert?.url?.translation[0]?.text
				} );
			} );
		} );

		return alerts;
	}

	const renderAlerts = ( alerts ) => {
		let alertOverview = '',
			alertDetail = '',
			alertCount = 0;

		if ( typeof tmMeta != 'undefined' ) {
			// loop through the agencies listed in tmMeta so that the agencies with alerts are sorted in the same order
			Object.keys( tmMeta ).forEach( key => {
				if ( !key.startsWith( "agency_" ) ) return;
				const agencyId = key.slice( 7 );
				if ( !alerts[agencyId] ) return;

				const agencyColor = Utilities.getStrokeColor( 'agency_' + agencyId );
				const agencyName = tmMeta[key]['name'];

				let agencyAlertDetail = '';

				alertOverview += `<li>
					<button type="button" data-open-panel="alert_detail" data-open-pane="agency-alerts|${agencyId}">
					<span class="title-with-agency-dot" style="--agency-color: ${agencyColor}">
						${agencyName}
					</span>
					</button>
					</li>`;

				alerts[agencyId].forEach( alert => {
					alertCount ++;
					let renderedRouteIds, renderedStopIds;

					if ( alert.routeIds.length ) {
						renderedRouteIds = `Route${alert.routeIds.length > 1 ? 's' : ''}: ` + alert.routeIds.map( routeId => tmMeta['route_' + routeId]?.name || '' ).filter( name => name ).join( ', ' );
					}

					if ( alert.stopIds.length ) {
						renderedStopIds = `Stop${alert.stopIds.length > 1 ? 's' : ''}: ${alert.stopIds.join( ', ' )}`;
					}

					agencyAlertDetail += `<li>
					<div class="tm__alert-header">${alert?.header}</div>
					${renderedRouteIds ? `<div class="tm__alert-entity">${renderedRouteIds}</div>` : ''}
					${renderedStopIds ? `<div class="tm__alert-entity">${renderedStopIds}</div>` : ''}
					<div class="tm__alert-description">${alert?.description}</div>
					${alert.url ? `<div class="tm__alert-link"><a href="${alert.url}" target="_blank">More information &gt;</a></div>` : ''}
					</li>`;
				} )

				agencyAlertDetail = `<div data-pane="agency-alerts|${agencyId}" aria-hidden="true" inert>
				<span class="title-with-agency-dot" style="--agency-color: ${agencyColor}">${agencyName}</span>
				<ul class="tm__alerts">` + agencyAlertDetail + `</ul>
				</div>`

				alertDetail += agencyAlertDetail;
			} )
		}

		return [alertOverview ? alertOverview : '<p class="tm__no-alerts">No active alerts</p>', alertDetail, alertCount];
	}

	const switchToPanel = ( panel, paneId ) => {
		switch ( panel ) {
			case "stop_detail":
				visibleStop = paneId;
				$nextArrival.hidden = true;
				fetchTripUpdates();
			// note: deliberately not breaking here, include everything from the route_detail case
			case "route_detail":
				updateVisibleTrips( paneId );
				initContinualGtfsUpdates();
				break;
			case "overview_bus_routes":
			case "agency_detail":
				clearInterval( intervalId );
				intervalId = null;
				visibleTrips = null;
				clearVehicleMarkers();
				break;
		}
	}

	const maybeInitRealtimeOnLoad = () => {
		const params = new URLSearchParams( location.search ),
			tab = params.get( "tab" ) ? params.get( "tab" ) : "bus_routes";

		if ( tab !== 'bus_routes' ) {
			return;
		}

		const agencyId = params.get( "agency" ) ? parseInt( params.get( "agency" ), 10 ) : null,
			routeId = params.get( "route" ) ? parseInt( params.get( "route" ), 10 ) : null,
			stopId = params.get( "stop" ) ? parseInt( params.get( "stop" ), 10 ) : null;

		if ( !agencyId || !routeId ) {
			return;
		}

		const panel = stopId ? 'stop_detail' : 'route_detail',
			pane = stopId ? stopId : routeId;

		if ( panel === 'stop_detail' ) {
			updateVisibleTrips( routeId );
		}

		switchToPanel( panel, pane );
	}

	const initContinualGtfsUpdates = () => {
		if ( intervalId ) return;
		continualGtfsUpdate();
		intervalId = setInterval( () => {
			continualGtfsUpdate()
		}, 10000 );
	}

	const continualGtfsUpdate = () => {
		if ( history.state && (history.state.bus_routes.panel === 'stop_detail' || history.state.bus_routes.panel === 'route_detail') ) {
			if ( history.state.tab === 'trip_planner' && $sidebar.dataset.hasDirectionsResults === 'true' ) {
				// if on the trip planner with visible directions, don't update, but keep checking to see if we might update again in future
				clearVehicleMarkers();
				return;
			}

			fetchTripUpdates();
			fetchVehiclePositions();
			return;
		}

		// if no longer on the route or stop detail panel, stop checking for updates
		clearInterval( intervalId );
		intervalId = null;
	}

	const getFeed = ( type ) => {
		const visible = TransitMap.getProperty( 'visible' );
		if ( gtfsRt[visible.agencyId] && gtfsRt[visible.agencyId][type] )
			return gtfsRt[visible.agencyId][type];
		return false;
	}

	const fetchTripUpdates = () => {
		if ( !visibleTrips ) return;

		const url = getFeed( 'trips' );
		if ( !url ) return;

		// skip updating if less than 30 seconds since last update
		if ( tripUpdates?.time && (Date.now() - tripUpdates.time < 1000 * 30) ) {
			updateStopArrivalTimes( tripUpdates.feedMessage );
			return;
		}

		fetch( url )
			.then( response => response.arrayBuffer() )
			.then( buffer => {
				const feedMessage = GtfsRealtimeBindings.transit_realtime.FeedMessage.decode(
					new Uint8Array( buffer )
				);

				tripUpdates = {
					time: Date.now(),
					feedMessage: feedMessage
				};

				updateStopArrivalTimes( feedMessage );
			} )
			.catch( err => {
				console.warn( 'Error:', err );
			} );
	}

	const updateStopArrivalTimes = ( feedMessage ) => {
		if ( !feedMessage ) return;

		const destinationSelected = parseInt( $dropdownDestination.value, 10 );

		let nextArrival = {
			seconds: null
		};

		// loop through all the trips and find which (if any) will visit this stop in the least amount of time
		feedMessage.entity.forEach( entity => {
			if ( !visibleTrips.includes( entity.tripUpdate.trip.tripId ) || entity.tripUpdate.trip.directionId !== destinationSelected ) {
				// wrong route or wrong direction
				return;
			}

			for ( const stop of entity.tripUpdate.stopTimeUpdate ) {
				if ( parseInt( stop.stopId, 10 ) !== visibleStop ) {
					// not the stop we're looking for
					continue;
				}

				// save this vehicle, unless it arrives after the already-saved vehicle
				const arrivalSeconds = stop.arrival.time - Date.now() / 1000;
				if ( !nextArrival.seconds || nextArrival.seconds > arrivalSeconds ) {
					nextArrival.seconds = arrivalSeconds;
				}
			}
		} );

		if ( nextArrival.seconds ) {
			// update the "Next Arrival" display
			const hours = Math.floor( nextArrival.seconds / 3600 ),
				minutes = Math.floor( (nextArrival.seconds % 3600) / 60 ),
				hoursStr = hours > 0 ? hours + '&nbsp;' + (hours > 1 ? tmVars.translations.hrs : tmVars.translations.hr) + ' ' : '',
				minutesStr = minutes + '&nbsp;' + tmVars.translations.min;
			$nextArrivalMinutes.innerHTML = hoursStr + minutesStr;
			$nextArrival.hidden = false;
		}
	}

	const fetchVehiclePositions = () => {
		if ( !visibleTrips ) return;

		const url = getFeed( 'pos' );
		if ( !url ) return;

		fetch( url )
			.then( response => response.arrayBuffer() )
			.then( buffer => {
				const feedMessage = GtfsRealtimeBindings.transit_realtime.FeedMessage.decode(
					new Uint8Array( buffer )
				);
				updateVehiclePositionMarkers( feedMessage );
			} )
			.catch( err => {
				console.error( 'Error:', err );
			} );
	}

	const updateVehiclePositionMarkers = ( feedMessage ) => {
		if ( !feedMessage || !visibleTrips ) return;

		clearVehicleMarkers();

		feedMessage.entity.forEach( entity => {
			if ( !visibleTrips.includes( entity.vehicle.trip.tripId ) || !entity.vehicle.position ) {
				// wrong route, or no position data
				return;
			}

			const marker = new google.maps.marker.AdvancedMarkerElement( {
				map: map,
				position: {lat: entity.vehicle.position.latitude, lng: entity.vehicle.position.longitude},
				content: getPinSvg( entity.vehicle.position?.bearing )
			} );

			realtimeMarkers.push( marker );
		} );
	}

	const updateVisibleTrips = ( routeId ) => {
		let tripIds = tmTrips[routeId];
		if ( !tripIds ) return null;
		visibleTrips = tripIds.split( ',' );
	}

	const clearVehicleMarkers = () => {
		for ( const marker of realtimeMarkers ) {
			marker.setMap( null );
		}
		realtimeMarkers = [];
	}

	return {
		init: () => {
			let $map = document.getElementById( 'tm__map' );
			if ( !$map ) return;

			if ( $map.dataset.loaded ) {
				constructor();
			} else {
				window.addEventListener( "tm__gmaps-loaded", constructor );
			}
		}
	}
})();

GtfsRealtime.init()
